import React from 'react';
import {
  NO_TARGET_SET,
  RAG_LABEL,
  gradeAgainstTarget,
  type RagThresholds,
  type RagTone,
} from '../../../hooks/pipelineTracker/targetsContract';

/**
 * One figure's line against its target: "Target $X · 95% On target", with a status dot.
 *
 * Shared by the average deal cells (PeopleTable, Board pack) and the net settlements panel so the
 * three say the same thing the same way. Colour is never the only signal: the grade is also a word,
 * as on the Deal movements tiles and the Operations glance row.
 *
 * THE THREE STATES, AND WHY THE FIRST IS WORDS. No target set is said in words, never shown as zero
 * or coloured: a zero target makes every figure read green, and a blank where a grade might be
 * reads as "fine". A target with no figure to compare (nothing settled, statements not loaded) shows
 * the target and says why it is not graded. Rendered only once the targets document has loaded, so
 * no line can claim "no target" while the answer is still on its way.
 */

const RAG_DOT: Record<RagTone, string> = {
  green: 'bg-green-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
};

const RAG_TEXT: Record<RagTone, string> = {
  green: 'text-green-300',
  amber: 'text-amber-300',
  red: 'text-red-300',
};

export interface TargetGradeLineProps {
  /** The target in the figure's unit (integer cents for money), or undefined for none set. */
  target: number | undefined;
  /** The figure graded, or null when there is none to grade. */
  actual: number | null;
  rag: RagThresholds;
  /** Formats the target in the unit it was set in. Exactly one place divides cents by 100. */
  format: (value: number) => string;
  /** Why there is no figure to grade, shown after the target when `actual` is null. */
  ungradedReason?: string;
  /** The words for no target. The default is the tiles' wording. */
  noTargetText?: string;
  className?: string;
}

export const TargetGradeLine: React.FC<TargetGradeLineProps> = ({
  target,
  actual,
  rag,
  format,
  ungradedReason,
  noTargetText = NO_TARGET_SET,
  className = '',
}) => {
  if (target === undefined) {
    return (
      <span data-testid="target-grade" data-rag="none" className={`block text-[0.6875rem] text-gray-500 ${className}`}>
        {noTargetText}
      </span>
    );
  }
  const grade = gradeAgainstTarget(actual, target, rag);
  if (grade === null) {
    return (
      <span data-testid="target-grade" data-rag="none" className={`block text-[0.6875rem] tabular-nums text-gray-500 ${className}`}>
        {`Target ${format(target)}`}
        {ungradedReason ? `, not graded: ${ungradedReason}` : ''}
      </span>
    );
  }
  return (
    <span
      data-testid="target-grade"
      data-rag={grade.tone}
      className={`flex items-center gap-1.5 text-[0.6875rem] tabular-nums leading-snug ${className}`}
    >
      <span aria-hidden="true" className={`h-2 w-2 shrink-0 rounded-full ${RAG_DOT[grade.tone]}`} />
      <span className={RAG_TEXT[grade.tone]}>{`Target ${format(target)} · ${grade.pct}%`}</span>
      <span className={`shrink-0 font-semibold ${RAG_TEXT[grade.tone]}`}>{RAG_LABEL[grade.tone]}</span>
    </span>
  );
};
